import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, 
  Package, 
  Users, 
  DollarSign,
  TrendingUp,
  Eye,
  MoreVertical,
  ChevronDown,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import Layout from '../components/Layout';
import StatsCard from '../components/StatsCard';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 258,
    totalSales: '₹1,28,450',
    products: 326,
    customers: 158
  });

  const recentOrders = [
    { id: '#H2025042501', customer: 'Ali Khan', amount: 1000, status: 'Delivered', date: '2024-04-25', items: 3 },
    { id: '#H2025042502', customer: 'Rahim Shahk', amount: 1450, status: 'Out for Delivery', date: '2024-04-25', items: 2 },
    { id: '#H2025042499', customer: 'Sana Pathan', amount: 320, status: 'Confirmed', date: '2024-04-24', items: 1 },
    { id: '#H2025042488', customer: 'Imran Ansari', amount: 780, status: 'Delivered', date: '2024-04-24', items: 4 },
    { id: '#H2025042487', customer: 'Faisal Memon', amount: 1500, status: 'Canceled', date: '2024-04-23', items: 5 },
  ];

  const recentProducts = [
    { name: 'Majun Arad Khurma', weight: '250 gm', stock: 'In Stock', price: '₹250', sales: 45 },
    { name: 'Sharbat Faulad', weight: '500 ml', stock: 'In Stock', price: '₹220', sales: 120 },
    { name: 'Habib-e-Sudanshan', weight: '120 Tablets', stock: 'In Stock', price: '₹180', sales: 80 },
    { name: 'Majun Shabab', weight: '250 gm', stock: 'Low Stock', price: '₹300', sales: 15 },
  ];

  const getStatusBadge = (status) => {
    const classes = {
      'Delivered': 'badge-success',
      'Out for Delivery': 'badge-warning',
      'Confirmed': 'badge-info',
      'Canceled': 'badge-danger',
    };
    return classes[status] || 'badge-info';
  };

  // Monthly sales data for chart
  const monthlySales = [
    { month: 'Jan', sales: 65000 },
    { month: 'Feb', sales: 78000 },
    { month: 'Mar', sales: 90000 },
    { month: 'Apr', sales: 85000 },
    { month: 'May', sales: 72000 },
    { month: 'Jun', sales: 95000 },
    { month: 'Jul', sales: 88000 },
    { month: 'Aug', sales: 70000 },
    { month: 'Sep', sales: 92000 },
    { month: 'Oct', sales: 80000 },
    { month: 'Nov', sales: 75000 },
    { month: 'Dec', sales: 82000 },
  ];

  // Calculate max value for chart scaling
  const maxSales = Math.max(...monthlySales.map(item => item.sales));

  return (
    <Layout>
      <div className="dashboard-page">
        {/* Page Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Dashboard</h1>
            <p className="page-subtitle">Welcome back! Here's your business overview.</p>
          </div>
          <div className="page-actions">
            <button className="btn btn-outline btn-sm">
              <Calendar size={16} />
              Last 30 Days
              <ChevronDown size={16} />
            </button>
            <button className="btn btn-primary">
              <TrendingUp size={18} />
              This Month
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <StatsCard 
            title="Total Orders" 
            value={stats.totalOrders} 
            icon={ShoppingBag}
            change="+12%"
            changeType="up"
            color="primary"
          />
          <StatsCard 
            title="Total Sales" 
            value={stats.totalSales} 
            icon={DollarSign}
            change="+18%"
            changeType="up"
            color="success"
          />
          <StatsCard 
            title="Products" 
            value={stats.products} 
            icon={Package}
            change="+8%"
            changeType="up"
            color="warning"
          />
          <StatsCard 
            title="Customers" 
            value={stats.customers} 
            icon={Users}
            change="+24%"
            changeType="up"
            color="info"
          />
        </div>

        {/* Dashboard Grid - Row 1 */}
        <div className="dashboard-grid grid-2">
          {/* Sales Chart */}
          <div className="card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Sales Overview</h3>
                <p className="card-subtitle">Monthly revenue breakdown</p>
              </div>
              <select className="filter-select" style={{ width: 'auto' }}>
                <option>This Year</option>
                <option>This Month</option>
                <option>Last Month</option>
              </select>
            </div>
            <div className="sales-chart">
              <div className="chart-container">
                <div className="chart-bars">
                  {monthlySales.map((item, index) => (
                    <div key={index} className="chart-bar-wrapper">
                      <div 
                        className="chart-bar" 
                        style={{ 
                          height: `${(item.sales / maxSales) * 100}%`,
                          background: `linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-light) 100%)`
                        }}
                      />
                      <div className="chart-tooltip">
                        <span className="tooltip-value">₹{item.sales.toLocaleString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="chart-labels">
                  {monthlySales.map((item, index) => (
                    <span key={index}>{item.month}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Recent Orders</h3>
                <p className="card-subtitle">Latest customer orders</p>
              </div>
              <button className="btn btn-sm btn-outline">View All</button>
            </div>
            <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="order-id">
                        <span className="order-id-link">{order.id}</span>
                      </td>
                      <td>{order.customer}</td>
                      <td className="order-amount">₹{order.amount.toLocaleString()}</td>
                      <td>
                        <span className={`badge ${getStatusBadge(order.status)}`}>
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Dashboard Grid - Row 2 */}
        <div className="dashboard-grid grid-2">
          {/* Top Products */}
          <div className="card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Top Products</h3>
                <p className="card-subtitle">Best selling products</p>
              </div>
              <button className="btn btn-sm btn-primary">View All</button>
            </div>
            <div className="product-list">
              {recentProducts.map((product, index) => (
                <div key={index} className="product-item">
                  <div className="product-info">
                    <div className="product-rank">{index + 1}</div>
                    <div className="product-avatar">
                      <span>🌿</span>
                    </div>
                    <div>
                      <p className="product-name">{product.name}</p>
                      <p className="product-detail">{product.weight}</p>
                    </div>
                  </div>
                  <div className="product-status">
                    <span className={`badge ${product.stock === 'In Stock' ? 'badge-success' : 'badge-warning'}`}>
                      {product.stock}
                    </span>
                    <span className="product-price">{product.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sales Report */}
          <div className="card">
            <div className="card-header">
              <div>
                <h3 className="card-title">Sales Report</h3>
                <p className="card-subtitle">Monthly performance</p>
              </div>
              <span className="badge badge-success">
                <ArrowUpRight size={14} />
                +22%
              </span>
            </div>
            <div className="sales-report">
              <div className="report-value">₹1,28,450</div>
              <p className="report-label">Total Sales</p>
              
              <div className="report-stats">
                <div className="report-stat">
                  <span className="stat-label">Revenue</span>
                  <span className="stat-value">₹1,28,450</span>
                  <span className="stat-change up">
                    <ArrowUpRight size={14} />
                    +18%
                  </span>
                </div>
                <div className="report-stat">
                  <span className="stat-label">Orders</span>
                  <span className="stat-value">258</span>
                  <span className="stat-change up">
                    <ArrowUpRight size={14} />
                    +12%
                  </span>
                </div>
                <div className="report-stat">
                  <span className="stat-label">Avg Order</span>
                  <span className="stat-value">₹498</span>
                  <span className="stat-change up">
                    <ArrowUpRight size={14} />
                    +6%
                  </span>
                </div>
              </div>

              <div className="report-footer">
                <div className="report-metric">
                  <span className="metric-label">This Month</span>
                  <span className="metric-value">₹1,28,450</span>
                  <span className="metric-change up">+22%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;